/**
 * Admin display helpers for the «Persian primary, technical secondary» rule (final QA §4): raw codes,
 * provider names and references are always available, but never the primary label.
 */
import { bankName } from '@hopex/identity';
import type { ReactNode } from 'react';
import { BANK_NAME_UNAVAILABLE } from './labels.ts';

/** Small LTR technical code under a Persian label. */
export function TechCode({ children }: { children: ReactNode }) {
  return (
    <bdi dir="ltr" className="block font-mono text-[11px] leading-5 text-fg-subtle" data-tech-code>
      {children}
    </bdi>
  );
}

/** Persian label first, raw code as secondary metadata. */
export function Labeled({ label, code }: { label: ReactNode; code?: string | null }) {
  return (
    <span className="flex flex-col">
      <span>{label}</span>
      {code ? <TechCode>{code}</TechCode> : null}
    </span>
  );
}

/**
 * Bank name from the public BIN / Sheba directory (@hopex/identity, OPEN-13 default: text names, no
 * logos). Unknown codes keep the honest fallback; the raw code stays as technical detail.
 */
export function BankName({ code }: { code: string | null }) {
  const name = bankName(code);
  if (name)
    return (
      <span className="flex items-center gap-2" data-bank-name>
        {code && /^[A-Z_]{2,20}$/.test(code) ? (
          <img
            src={`/banks/${code}.svg`}
            alt=""
            aria-hidden
            loading="lazy"
            className="size-7 shrink-0 rounded-full bg-white object-contain p-0.5"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : null}
        <span className="flex flex-col">
          <span>{name}</span>
          {code ? <TechCode>کد بانک: {code}</TechCode> : null}
        </span>
      </span>
    );
  return (
    <span className="flex flex-col" data-bank-name-unavailable>
      <span className="text-fg-muted">{BANK_NAME_UNAVAILABLE}</span>
      {code ? <TechCode>کد بانک: {code}</TechCode> : null}
    </span>
  );
}
